import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft, Shield, ShieldCheck, UserMinus, Crown, Building2,
  UserPlus, Pencil, DollarSign, TrendingUp, Plus, Users, Activity,
  FileText, ToggleLeft, ToggleRight, Trash2, Save, X, Eye
} from "lucide-react";

import CompanyService from "../../services/company-service";

// ==================== Status Badges ====================

const StatusBadge = ({ status, size = "sm" }) => {
  const colors = {
    Active: "bg-green-100 text-green-800 border-green-200",
    Pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
    Inactive: "bg-red-100 text-red-800 border-red-200",
  };
  const sizeClass = size === "lg" ? "px-3 py-1 text-sm" : "px-2 py-0.5 text-xs";
  return (
    <span className={`${sizeClass} rounded-full font-medium border ${colors[status] || "bg-gray-100 text-gray-800 border-gray-200"}`}>
      {status}
    </span>
  );
};

const RoleBadge = ({ role }) => {
  const colors = {
    Creator: "bg-purple-100 text-purple-800",
    Editor: "bg-blue-100 text-blue-800",
    ListedMember: "bg-gray-100 text-gray-800",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[role] || "bg-gray-100"}`}>
      {role}
    </span>
  );
};

// ==================== Tab: Profile ====================

const ProfileTab = ({ uuid, companyData, onRefresh }) => {
  const [reviewLoading, setReviewLoading] = useState(false);

  const handleReviewStatus = async (newStatus) => {
    const confirmMsg = newStatus === "Active"
      ? "Approve this company? All pending members will be activated and their email domains will be verified."
      : newStatus === "Inactive"
      ? "Deactivate this company?"
      : "Set this company to pending?";

    if (!window.confirm(confirmMsg)) return;

    setReviewLoading(true);
    try {
      await CompanyService.updateReviewStatus(uuid, newStatus);
      toast.success(`Company status changed to ${newStatus}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update status");
    } finally {
      setReviewLoading(false);
    }
  };

  if (!companyData) return <div className="text-center py-10 text-gray-500">Loading...</div>;

  const displayName = companyData.organization || companyData.name || "(unnamed)";

  return (
    <div className="space-y-6">
      {/* Review Status Banner */}
      {companyData.review_status === "Pending" && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-yellow-800 flex items-center gap-2">
                <Shield size={18} /> Under Review
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                Please verify the creator's email domain before approving.
                {companyData.submitted_at && (
                  <span className="ml-2">Submitted: {companyData.submitted_at}</span>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleReviewStatus("Active")}
                disabled={reviewLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                <ShieldCheck size={16} /> Approve
              </button>
              <button
                onClick={() => handleReviewStatus("Inactive")}
                disabled={reviewLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company Management Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Company Management</h2>
          <StatusBadge status={companyData.review_status} size="lg" />
        </div>

        {companyData.review_status === "Active" && (
          <div className="flex gap-2">
            <button
              onClick={() => handleReviewStatus("Inactive")}
              disabled={reviewLoading}
              className="px-3 py-1.5 border border-red-300 text-red-600 rounded hover:bg-red-50 disabled:opacity-50 text-sm flex items-center gap-1"
            >
              <ToggleLeft size={16} /> Set Inactive
            </button>
          </div>
        )}
        {companyData.review_status === "Inactive" && (
          <button
            onClick={() => handleReviewStatus("Active")}
            disabled={reviewLoading}
            className="px-3 py-1.5 border border-green-300 text-green-600 rounded hover:bg-green-50 disabled:opacity-50 text-sm flex items-center gap-1"
          >
            <ToggleRight size={16} /> Reactivate
          </button>
        )}
      </div>

      {/* Profile Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Profile</h2>
        <div className="grid grid-cols-2 gap-4">
          <InfoField label="Organization Name" value={displayName} />
          <InfoField label="Legal Name" value={companyData.legal_name} />
          <InfoField label="Original Name" value={companyData.original_name} />
          <InfoField label="Company Type" value={companyData.organization_type || companyData.company_type} />
          <InfoField label="Primary Field" value={formatJsonField(companyData.primary_field)} />
          <InfoField label="Website" value={companyData.website} link />
          <InfoField label="Headquarters" value={companyData.address} />
          <InfoField label="Region / Country" value={companyData.region || companyData.country_code} />
          <InfoField label="Founded Date" value={companyData.founded_date} />
          <InfoField label="Founders" value={formatJsonField(companyData.founders)} />
          <InfoField label="CEO" value={companyData.CEO} />
          <InfoField label="Employees" value={companyData.num_employees} />
        </div>
      </div>

      {/* Creator Info */}
      {(companyData.creator_name || companyData.creator_email) && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Creator Info</h2>
          <div className="grid grid-cols-2 gap-4">
            <InfoField label="Creator Name" value={companyData.creator_name} />
            <InfoField label="Creator Email" value={companyData.creator_email} />
          </div>
        </div>
      )}

      {/* Verified Domains */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Verified Email Domains</h2>
        {companyData.verified_email_domains ? (
          <div className="flex flex-wrap gap-2">
            {(() => {
              try {
                const domains = typeof companyData.verified_email_domains === 'string'
                  ? JSON.parse(companyData.verified_email_domains)
                  : companyData.verified_email_domains;
                if (Array.isArray(domains)) {
                  return domains.map((d, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm border border-blue-200">
                      @{d}
                    </span>
                  ));
                }
              } catch {}
              return <span className="text-gray-500 text-sm">{String(companyData.verified_email_domains)}</span>;
            })()}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">No verified domains yet</span>
        )}
      </div>

      {/* Description */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Company Description</h2>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-500">About</label>
            <p className="text-sm mt-1 whitespace-pre-wrap">{companyData.full_description || companyData.about || "-"}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoField = ({ label, value, link = false }) => (
  <div>
    <label className="text-sm font-medium text-gray-500">{label}</label>
    {link && value ? (
      <a href={value} target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline mt-0.5">{value}</a>
    ) : (
      <p className="text-sm mt-0.5">{value || <span className="text-gray-300">-</span>}</p>
    )}
  </div>
);

const formatJsonField = (val) => {
  if (!val) return null;
  if (Array.isArray(val)) return val.join(", ");
  if (typeof val === "string") {
    try {
      const arr = JSON.parse(val);
      if (Array.isArray(arr)) return arr.join(", ");
    } catch {}
  }
  return String(val);
};

// ==================== Tab: Teams ====================

const TeamsTab = ({ uuid }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [noteValue, setNoteValue] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [promoteConfirm, setPromoteConfirm] = useState(null);
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchText, setSearchText] = useState("");

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const res = await CompanyService.getTeams(uuid);
      setMembers(res.data);
    } catch (err) {
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (uuid) fetchTeams(); }, [uuid]);

  const handleApprove = async (memberId, addDomain) => {
    setActionLoading(memberId);
    try {
      await CompanyService.approveMember(uuid, memberId, addDomain);
      toast.success("Member approved");
      fetchTeams();
      if (selectedMember?.memberId === memberId) {
        setSelectedMember(prev => prev ? { ...prev, status: "Active" } : null);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to approve");
    } finally { setActionLoading(null); }
  };

  const handleReject = async (memberId) => {
    setActionLoading(memberId);
    try {
      await CompanyService.rejectMember(uuid, memberId);
      toast.success("Member rejected");
      setSelectedMember(null);
      setDeleteConfirm(null);
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to reject");
    } finally { setActionLoading(null); }
  };

  const handleDelete = async (memberId) => {
    setActionLoading(memberId);
    try {
      await CompanyService.rejectMember(uuid, memberId);
      toast.success("Member removed");
      setSelectedMember(null);
      setDeleteConfirm(null);
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to remove");
    } finally { setActionLoading(null); }
  };

  const handlePromote = async (memberId) => {
    setActionLoading(memberId);
    try {
      await CompanyService.promoteCreator(uuid, memberId);
      toast.success("Creator promoted");
      setPromoteConfirm(null);
      setSelectedMember(null);
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to promote");
    } finally { setActionLoading(null); }
  };

  const handleSaveNote = async () => {
    if (!selectedMember) return;
    setNoteLoading(true);
    try {
      await CompanyService.updateMemberNote(uuid, selectedMember.memberId, noteValue);
      toast.success("Note saved");
      fetchTeams();
    } catch (err) {
      toast.error("Failed to save note");
    } finally { setNoteLoading(false); }
  };

  const openMemberDetail = (m) => {
    setSelectedMember(m);
    setNoteValue(m.note || "");
  };

  if (loading) return <div className="text-center py-10 text-gray-500">Loading...</div>;

  const pendingCount = members.filter(m => m.status === "Pending").length;

  // 篩選
  const filtered = members.filter(m => {
    if (roleFilter && m.role !== roleFilter) return false;
    if (statusFilter && m.status !== statusFilter) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      if (!m.name?.toLowerCase().includes(q) && !m.email?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // 成員詳情面板
  let content;
  if (selectedMember) {
    const m = selectedMember;
    const isPending = m.status === "Pending";

    content = (
      <div className="space-y-4">
        <button onClick={() => setSelectedMember(null)} className="flex items-center gap-1 text-gray-500 hover:text-black text-sm">
          <ArrowLeft size={16} /> Back to list
        </button>

        {isPending && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-yellow-800 flex items-center gap-2">
                  <Shield size={18} /> Pending Review
                </h3>
                <p className="text-sm text-yellow-700 mt-1">This member is awaiting approval.</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleApprove(m.memberId, true)} disabled={actionLoading === m.memberId}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50">
                  Approve
                </button>
                <button onClick={() => handleReject(m.memberId)} disabled={actionLoading === m.memberId}
                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50">
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Profile:</span>
              <StatusBadge status={m.status} size="lg" />
            </div>
            <div className="flex gap-2">
              {!isPending && m.role !== "Creator" && (
                <button onClick={() => setPromoteConfirm(m.memberId)} disabled={actionLoading === m.memberId}
                  className="px-2 py-1 border rounded text-xs hover:bg-gray-50 flex items-center gap-1">
                  <Crown size={12} /> Promote
                </button>
              )}
              {m.role !== "Creator" && (
                <button onClick={() => setDeleteConfirm(m.memberId)}
                  className="px-2 py-1 border border-red-300 text-red-600 rounded text-xs hover:bg-red-50 flex items-center gap-1">
                  <Trash2 size={12} /> Delete
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Team Member Name*</label>
              <p className="text-sm mt-0.5">{m.name || "-"}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email*</label>
              <p className="text-sm mt-0.5">
                <a href={`/member/account/${m.frontendUserId}`} className="text-blue-600 hover:underline">{m.email}</a>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Role*</label>
              <p className="text-sm mt-0.5"><RoleBadge role={m.role} /></p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Job Title*</label>
              <p className="text-sm mt-0.5">{m.jobTitle || "-"}</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Note</label>
            <p className="text-xs text-gray-400 mb-1">這裡人事變動等相關資訊，其他人也可以讀取</p>
            <textarea
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              rows={4}
              placeholder="Add notes about this member..."
              className="w-full p-2 border rounded border-gray-300 text-sm resize-y"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => setSelectedMember(null)} className="px-4 py-1.5 border rounded text-sm hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleSaveNote} disabled={noteLoading}
              className="px-4 py-1.5 bg-black text-white rounded text-sm hover:bg-gray-800 disabled:opacity-50">
              {noteLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  } else {

  // 列表
  content = (
    <div className="space-y-4">
      {pendingCount > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
          <Shield size={18} className="text-yellow-600" />
          <span className="text-sm text-yellow-800 font-medium">
            {pendingCount} member{pendingCount > 1 ? "s" : ""} pending review
          </span>
        </div>
      )}

      {/* 篩選 */}
      <div className="flex items-center gap-3">
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="px-2 py-1 border rounded text-xs focus:outline-none">
          <option value="">All Roles</option>
          <option value="Creator">Creator</option>
          <option value="Editor">Editor</option>
          <option value="ListedMember">Listed Member</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1 border rounded text-xs focus:outline-none">
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Pending">Pending</option>
        </select>
        <input type="text" placeholder="Search member..." value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="px-2 py-1 border rounded text-xs w-48 focus:outline-none" />
        <span className="ml-auto text-xs text-gray-400">{filtered.length} members</span>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-10 text-gray-500">No members found</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((m) => (
                <tr key={m.memberId} className={`hover:bg-gray-50 ${m.status === "Pending" ? "bg-yellow-50/50" : ""}`}>
                  <td className="px-4 py-3 text-xs text-gray-500">{m.memberId}</td>
                  <td className="px-4 py-3 text-sm font-medium">{m.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{m.email}</td>
                  <td className="px-4 py-3 text-sm">{m.jobTitle || "-"}</td>
                  <td className="px-4 py-3"><RoleBadge role={m.role} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(m.joinedAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => openMemberDetail(m)}
                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center gap-1">
                        <Eye size={12} /> View
                      </button>
                      {m.status === "Active" && m.role !== "Creator" && (
                        <button onClick={() => setPromoteConfirm(m.memberId)} disabled={actionLoading === m.memberId}
                          className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1">
                          <Crown size={12} />
                        </button>
                      )}
                      {m.role !== "Creator" && (
                        <button onClick={() => setDeleteConfirm(m.memberId)}
                          className="text-gray-300 hover:text-red-500 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
  } // end else (list mode)

  // Modals 放在最外層，詳情/列表模式都能顯示
  return (
    <>
      {content}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
            <p className="text-sm text-gray-700 mb-4">你確定要刪除此公司的 Team Member？</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)}
                className="px-4 py-1.5 border rounded text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading === deleteConfirm}
                className="px-4 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Promote Confirmation Modal */}
      {promoteConfirm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
            <p className="text-sm text-gray-700 mb-4">確定要將此成員提升為 Creator？現有的 Creator 將會變成 Editor。</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPromoteConfirm(null)}
                className="px-4 py-1.5 border rounded text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={() => handlePromote(promoteConfirm)}
                disabled={actionLoading === promoteConfirm}
                className="px-4 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50">Promote</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== Tab: Activity ====================

const ACTIVITY_CONFIG = {
  CreateCompany: { icon: Building2, color: "bg-green-500", label: "Create Company" },
  EditCompanyProfile: { icon: Pencil, color: "bg-blue-500", label: "Edit Company Profile" },
  CreateFoundingHistory: { icon: DollarSign, color: "bg-orange-500", label: "Create Founding History" },
  CreateInvestmentHistory: { icon: TrendingUp, color: "bg-teal-500", label: "Create Investment History" },
  AddTeamMember: { icon: UserPlus, color: "bg-purple-500", label: "Add Team Member" },
  EditTeamMember: { icon: Pencil, color: "bg-indigo-500", label: "Edit Team Member" },
  RemoveMember: { icon: UserMinus, color: "bg-red-500", label: "Remove Member" },
};

const ActivityTab = ({ uuid }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uuid) return;
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await CompanyService.getActivity(uuid);
        setLogs(res.data);
      } catch { toast.error("Failed to load activity"); }
      finally { setLoading(false); }
    };
    fetch();
  }, [uuid]);

  if (loading) return <div className="text-center py-10 text-gray-500">Loading...</div>;
  if (logs.length === 0) return <div className="text-center py-10 text-gray-500">No activity found</div>;

  return (
    <div className="space-y-3">
      {logs.map((log) => {
        const config = ACTIVITY_CONFIG[log.activityType] || { icon: Plus, color: "bg-gray-500", label: log.activityType };
        const Icon = config.icon;
        return (
          <div key={log.id} className="flex items-start gap-4 bg-white rounded-lg shadow p-4">
            <div className={`${config.color} p-2 rounded-full text-white flex-shrink-0`}>
              <Icon size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="truncate">
                  <span className="font-medium text-sm">{log.userName}</span>
                  <span className="text-gray-400 text-xs ml-2">{log.userEmail}</span>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 ml-4">
                  {new Date(log.createdAt).toLocaleString()}
                </span>
              </div>
              <p className="text-sm text-gray-700 mt-1">{config.label}</p>
              {log.metadata && (
                <p className="text-xs text-gray-500 mt-1 font-mono bg-gray-50 px-2 py-1 rounded truncate">
                  {log.metadata}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==================== Main Detail Page ====================

const TABS = [
  { key: "profile", label: "Profile", icon: FileText },
  { key: "teams", label: "Teams", icon: Users },
  { key: "activity", label: "Teams Activity", icon: Activity },
];

const CompanyDetail = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchCompany = async () => {
    try {
      setLoading(true);
      const res = await CompanyService.getDetails(uuid);
      setCompanyData(res.data);
    } catch (err) {
      toast.error("Failed to load company");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const res = await CompanyService.getTeams(uuid);
      const pending = res.data.filter(m => m.status === "Pending").length;
      setPendingCount(pending);
    } catch {}
  };

  useEffect(() => {
    if (uuid) {
      fetchCompany();
      fetchPendingCount();
    }
  }, [uuid]);

  const displayName = companyData?.organization || companyData?.name || "(loading...)";

  return (
    <div className="p-6 pt-0">
      <div className="container mx-auto">
        {/* Header */}
        <div className="sticky top-0 pt-6 pb-4 bg-white z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-500 hover:text-black transition-colors">
                <ArrowLeft size={20} /> <span className="text-sm">Back</span>
              </button>
              <h1 className="text-xl font-bold">{displayName}</h1>
              {companyData && <StatusBadge status={companyData.review_status} size="lg" />}
            </div>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  if (!window.confirm("Delete this company? This cannot be undone.")) return;
                  try {
                    await CompanyService.delete(uuid);
                    toast.success("Company deleted");
                    navigate("/company/profile");
                  } catch (err) {
                    toast.error(err.response?.data?.error || "Failed to delete");
                  }
                }}
                className="px-3 py-1.5 border border-red-300 text-red-600 rounded text-sm hover:bg-red-50 flex items-center gap-1"
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
                    ${isActive
                      ? "border-black text-black"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  <Icon size={16} />
                  {tab.label}
                  {tab.key === "teams" && pendingCount > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full leading-none">
                      {pendingCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-4">
          {loading && activeTab === "profile" ? (
            <div className="text-center py-10 text-gray-500">Loading...</div>
          ) : (
            <>
              {activeTab === "profile" && <ProfileTab uuid={uuid} companyData={companyData} onRefresh={() => { fetchCompany(); fetchPendingCount(); }} />}
              {activeTab === "teams" && <TeamsTab uuid={uuid} />}
              {activeTab === "activity" && <ActivityTab uuid={uuid} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyDetail;
